import { Component, type ErrorInfo, type ReactNode } from 'react';

export interface ErrorBoundaryProps {
  /** O que renderizar quando o filho quebra. Recebe nada: o fallback nao deve depender do erro. */
  readonly fallback: ReactNode;
  readonly children: ReactNode;
  /** Nome curto do trecho, para o log. Ajuda a achar o culpado num bundle minificado. */
  readonly area: string;
}

interface ErrorBoundaryState {
  readonly quebrou: boolean;
}

/**
 * Isola um trecho da tela para que um erro dele nao derrube a arvore inteira.
 *
 * ESTA APLICACAO NAO TINHA NENHUM, E O CODIGO JA SABIA DISSO: dois comentarios —
 * `features/enrollment/StudentDetailPage.tsx` e o teste ao lado dele — dizem, com todas
 * as letras, "sem ErrorBoundary a queda e a pagina inteira" e "sem ErrorBoundary, pagina
 * em branco". Eram observacoes corretas sobre um risco que ninguem tinha fechado.
 *
 * O QUE FECHOU ELE: a inscricao no segundo fator caiu em tela branca em producao. A API
 * respondeu 201 com o segredo, o React quebrou ao renderizar o QR code, e a pessoa ficou
 * sem NADA na tela — nem o segredo, que estava logo abaixo e era o que ela precisava
 * copiar. Um erro num grafico decorativo apagou o caminho inteiro de entrada na
 * plataforma.
 *
 * NAO E PARA ENVOLVER A APLICACAO INTEIRA. Um boundary na raiz troca a tela branca por
 * uma mensagem generica, que e melhor, mas continua sem entregar o que a pessoa veio
 * fazer. Ele serve para isolar o trecho SUBSTITUIVEL — aquele cujo fallback ainda
 * resolve o problema de quem esta olhando.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { quebrou: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { quebrou: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // O console e o unico lugar que resta: nao ha coletor de erro de navegador nesta
    // fase. A area entra na mensagem porque o bundle de producao e minificado e o nome
    // do componente no stack nao diz nada.
    console.error(`[ErrorBoundary:${this.props.area}]`, error, info.componentStack);
  }

  override render(): ReactNode {
    return this.state.quebrou ? this.props.fallback : this.props.children;
  }
}
